import React, { useState, useRef, useEffect, useMemo } from "react";
import Header from "../../../components/Header/Header";
import images from "../../../images";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetQuestTakeResultQuery,
  useGetQuestDetailsQuery,
} from "../../../services/apiSlice";
import TakeResultStep1 from "./TakeResultStep/TakeResultStep1";
import TakeResultStep2 from "./TakeResultStep/TakeResultStep2";
import TakeResultStep3 from "./TakeResultStep/TakeResultStep3";
import TakeResultStep4 from "./TakeResultStep/TakeResultStep4";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";
import debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";

const colors = [
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#4BC0C0",
  "#9966FF",
  "#FF9F40",
  "#2ECC71",
  "#D81B60",
  "#3498DB",
];

const StepIndicator = ({ activeStep, totalSteps }) => (
  <div className="flex gap-1 items-center self-center mb-3 mt-3">
    {Array.from({ length: totalSteps }, (_, index) => (
      <div
        key={index}
        className={`flex shrink-0 rounded-xl h-[4px] ${
          activeStep === index + 1 ? "bg-indigo-600" : "bg-stone-200"
        }`}
        style={{ width: index + 1 === activeStep ? "20px" : "12px" }}
      />
    ))}
  </div>
);

const TakeQuestResult = () => {
  const [state, setState] = useState({
    activeTab: "age",
    isMenuOpen: false,
    page: 1,
    limit: 5,
    hasMore: true,
    selectedLocation: "",
  });
  const [selectedInsightRecommendation, setSelectedInsightRecommendation] =
    useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();

  const { data: questTakeResultData, isLoading: isLoadingObjectiveData } =
    useGetQuestTakeResultQuery(
      {
        quest_business_id: id,
        ...(state.selectedLocation !== "" && {
          location_id: state.selectedLocation,
        }),
      },
      {
        skip: !id,
        refetchOnMountOrArgChange: true,
      }
    );

  const { data: questDetailsData, isLoading: isLoadingDetails } =
    useGetQuestDetailsQuery({
      quest_id: id,
    });

  const locationDropdownRef = useRef(null);

  const prepareChartData = () => {
    if (!questTakeResultData?.data?.segmentation_results) {
      return { data: [], labels: [], colors: colors.slice(0, 4) };
    }

    const segmentationResults = questTakeResultData.data.segmentation_results;
    const activeSegmentation = segmentationResults.find(
      (seg) => seg.field === state.activeTab
    );

    if (!activeSegmentation || !activeSegmentation.statistics) {
      return { data: [], labels: [], colors: colors.slice(0, 4) };
    }

    return {
      data: activeSegmentation.statistics.map((stat) => stat.percentage || 0),
      labels: activeSegmentation.statistics.map((stat) => stat.option || ""),
      colors: colors.slice(0, activeSegmentation.statistics.length),
    };
  };

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const displayedTotalSteps = 3;

  const handleNextStep = () => {
    if (currentStep === 3) {
      navigate("/");
    } else if (currentStep === 4) {
      setCurrentStep((prevStep) => Math.max(prevStep - 1, 1));
    } else {
      setCurrentStep((prevStep) => Math.min(prevStep + 1, totalSteps));
    }
  };

  const handlePrevStep = () => {
    setCurrentStep((prevStep) => Math.max(prevStep - 1, 1));
  };

  const handleAction = () => {
    setCurrentStep(4);
  };

  const handleRecommendation = (insight) => {
    setSelectedInsightRecommendation(insight.recommendation);
    setCurrentStep(4);
  };

  const handleTabChange = (tabValue) => {
    setState((prev) => ({
      ...prev,
      activeTab: tabValue,
      page: 1,
      limit: 5,
      hasMore: true,
    }));
  };

  const debouncedHandleLocationSelect = useMemo(
    () =>
      debounce((locationId) => {
        setState((prev) => ({
          ...prev,
          selectedLocation: locationId,
        }));
      }, 500),
    []
  );

  const handleLocationSelect = (locationId) => {
    debouncedHandleLocationSelect(locationId);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        locationDropdownRef.current &&
        !locationDropdownRef.current.contains(event.target)
      ) {
        setState((prev) => ({
          ...prev,
          isMenuOpen: false,
        }));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const chartData = prepareChartData();
  const questLocations = questDetailsData?.data?.quest_locations || [];

  const handleShare = async () => {
    const questName = questDetailsData?.data?.name || "Quest Results";
    const shareText =
      questTakeResultData?.data?.insights?.[0]?.recommendation ||
      `Check out the results for ${questName}!`;
    const shareUrl = window.location.href;

    const shareableUrl = new URL(shareUrl);
    shareableUrl.searchParams.set("shared", "true");

    if (navigator.share) {
      try {
        await navigator.share({
          title: questName,
          text: shareText,
          url: shareableUrl.toString(),
        });
      } catch (error) {}
    } else {
      const tempInput = document.createElement("input");
      document.body.appendChild(tempInput);
      tempInput.value = shareableUrl.toString();
      tempInput.select();
      document.execCommand("copy");
      document.body.removeChild(tempInput);

      alert("Đã sao chép liên kết vào clipboard: " + shareableUrl.toString());
    }
  };

  return (
    <main className="flex flex-col mx-auto w-full bg-white max-w-[480px] min-h-screen">
      <LoadingCommon
        statusLoading={isLoadingObjectiveData || isLoadingDetails}
      />
      <section className="sticky top-0 z-10 flex flex-col justify-center px-4 pb-4 w-full bg-[#3F54DF] rounded-none">
        <Header
          title={
            currentStep === 4
              ? t("quest.questResult.action")
              : t("quest.questResult.questResult")
          }
          iconRespon={
            currentStep === 1
              ? images.GroupIconQuestResult
              : images.QuestionIconQuestResult
          }
          textRespon={
            currentStep === 1
              ? t("quest.questResult.whoResponded")
              : currentStep === 2
              ? t("quest.question")
              : t("quest.questResult.keyInsights")
          }
          totalSteps={currentStep === 4 ? null : displayedTotalSteps}
          currentStep={
            currentStep === 4
              ? null
              : Math.min(currentStep, displayedTotalSteps)
          }
        />
      </section>

      <div className="flex flex-col p-3 w-full">
        {currentStep === 1 && (
          <TakeResultStep1
            activeTab={state.activeTab}
            selectedLocation={state.selectedLocation}
            handleTabChange={handleTabChange}
            handleLocationSelect={handleLocationSelect}
            chartData={chartData}
            questLocations={questLocations}
            segmentationResults={
              questTakeResultData?.data?.segmentation_results || []
            }
          />
        )}
        {currentStep === 2 && <TakeResultStep2 result={questTakeResultData} />}
        {currentStep === 3 && (
          <TakeResultStep3
            handleAction={handleAction}
            result={questTakeResultData}
            handleRecommendation={handleRecommendation}
          />
        )}
        {currentStep === 4 && (
          <TakeResultStep4
            result={questTakeResultData}
            selectedInsightRecommendation={selectedInsightRecommendation}
          />
        )}
        {currentStep !== 4 && (
          <StepIndicator
            activeStep={Math.min(currentStep, displayedTotalSteps)}
            totalSteps={displayedTotalSteps}
          />
        )}

        <div className="flex gap-2 px-3 mt-3 w-full">
          {currentStep !== 4 ? (
            <>
              <button
                onClick={handlePrevStep}
                disabled={currentStep === 1}
                className={`flex-1 flex items-center justify-center px-4 py-2 text-sm bg-white rounded-xl border border-stone-200 ${
                  currentStep === 1 ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <img
                  src={images.PrevIcon}
                  alt="Prev"
                  className="w-5 h-5 mr-1"
                />
                <span>{t("base.prev")}</span>
              </button>
              <button
                onClick={handleNextStep}
                className={`flex-1 flex items-center justify-center px-4 py-2 text-sm text-white bg-[#3F54DF] rounded-xl `}
              >
                <span>
                  {currentStep === 3 || currentStep === totalSteps
                    ? t("base.close")
                    : t("base.next")}
                </span>
                <img
                  src={images.NextWhiteIcon}
                  alt="Next"
                  className="w-5 h-5 ml-1"
                />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleShare}
                className="flex-1 flex items-center justify-center px-4 py-2 text-sm text-white bg-gradient-to-r from-blue-600 to-blue-400 rounded-xl hover:opacity-90 gap-1"
              >
                <img
                  src={images.ShareIcon}
                  alt="Share"
                  className="w-5 h-5 ml-1"
                />
                <span>{t("base.share")}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export default TakeQuestResult;
